import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

const setupScene = () => {
  const scene = new THREE.Scene();
  scene.background = null;
  return scene;
};

const setupCamera = () => {
    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      2000
    );
    camera.position.set(0, 2, 5);
    camera.lookAt(0, -0.5, 0);
    return camera;
  };

const setupRenderer = () => {
  const renderer = new THREE.WebGLRenderer({ 
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true
  });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.shadowMap.enabled = true;
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setClearColor(0x000000, 0); // Ensure transparency
  return renderer;
};

const setupLights = (scene) => {
  // Ambient light with warmer tone
  const ambientLight = new THREE.AmbientLight(0xffffff, 2);
  scene.add(ambientLight);

  // Main directional light positioned for better highlights
  const directionalLight = new THREE.DirectionalLight(0xffffff, 1.5);
  directionalLight.position.set(0, 8, 0);
  directionalLight.castShadow = false;
  scene.add(directionalLight);
};

const loadTexture = () => {
  return new Promise((resolve, reject) => {
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load(
      `${process.env.PUBLIC_URL}/Christmas_Texture_Color.jpg`,
      resolve,
      (xhr) => console.log((xhr.loaded / xhr.total * 100) + '% loaded texture'),
      reject
    );
  });
};

const loadModel = (scene, texture) => {
  return new Promise((resolve, reject) => {
    const fbxLoader = new FBXLoader();
    fbxLoader.load(
      `${process.env.PUBLIC_URL}/3d-tree.fbx`,
      (model) => {
        model.traverse((child) => {
          if (child.isMesh) {
            child.material.metalness = 0;
            child.material.roughness = 0.7;
            child.material.map = texture;
            child.material.needsUpdate = true;
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });

        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center);
        
        // Adjusted scale and position for better framing
        model.scale.set(0.012, 0.012, 0.012);
        model.position.set(0, -3, 0);
        model.rotation.y = Math.PI / 4;

        scene.add(model);
        resolve(model);
      },
      (xhr) => console.log((xhr.loaded / xhr.total * 100) + '% loaded model'),
      reject
    );
  });
};

const ThreeScene = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const scene = setupScene();
    const camera = setupCamera();
    const renderer = setupRenderer();
    
    mountRef.current.style.position = 'absolute';
    mountRef.current.style.top = '0';
    mountRef.current.style.left = '0';
    mountRef.current.style.width = '100%';
    mountRef.current.style.height = '100%';
    mountRef.current.style.pointerEvents = 'auto';
    
    mountRef.current.appendChild(renderer.domElement);
    setupLights(scene);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableZoom = false;
    controls.enablePan = false;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 4; 
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    
    controls.minPolarAngle = Math.PI / 3;
    controls.maxPolarAngle = Math.PI / 2;

    const initializeScene = async () => {
      try {
        const texture = await loadTexture();
        const model = await loadModel(scene, texture);
        
        const animate = () => {
          requestAnimationFrame(animate);
          controls.update();
          renderer.render(scene, camera);
        };
        animate();
      } catch (error) {
        console.error('Error loading assets:', error);
      }
    };

    initializeScene();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
      controls.dispose();
    };
  }, []);

  return <div ref={mountRef} />;
};

export default ThreeScene;